import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";

import { createAxiosInstance } from "api/axiosConfig";

export const transformOCFDataFromAPI = (nestedData) => {
  const { clinicalData } = nestedData;
  const {
    indications,
    riskFactors,
    physicalExam,
    laboratoryInvestigations,
    ocfOutcome,
    patientDemographics,
  } = clinicalData;

  return {
    ethnicityCode: patientDemographics?.ethnicityCode,
    registeredDate: new Date(nestedData.reviewDate),
    reviewingDate: new Date(nestedData.reviewDate),
    reviewingDoctor: nestedData.doctorName,
    doctorId: nestedData.doctorId ?? null,
    createdBy: nestedData.doctorName,
    reviewDate: nestedData.reviewDate ?? null,
    consultationDate: nestedData.reviewDate
      ? format(new Date(nestedData.reviewDate), "dd-MM-yyyy")
      : format(new Date(), "dd-MM-yyyy"),
    eircode: patientDemographics?.eircode ?? "",
    indications: clinicalData.registrationReasons,
    medications: [],
    date: new Date(nestedData.reviewDate),
    reviewType:
      nestedData.reviewType === "OCF_INITIAL"
        ? "OCF Initial"
        : nestedData.reviewType,
    status: nestedData.status ?? nestedData.clinicalMessageStatus,
    statusErrors: nestedData.statusErrors ?? [],
    statusWarnings: nestedData.statusWarnings ?? [],
    clinicalMessageStatus: nestedData.clinicalMessageStatus ?? null,
    clinicalMessageControlId: nestedData.clinicalMessageControlId ?? null,
    clinicalMessageXml: nestedData.clinicalMessageXml ?? null,
    reimbursementMessageStatus: nestedData.reimbursementMessageStatus ?? null,
    reimbursementMessageControlId: nestedData.reimbursementMessageControlId ?? null,

    currentSmokingStatus: indications?.currentSmokingStatus,
    bmiGreaterThan30: indications?.bmiGreaterThan30,
    previousHighBnp: indications?.previousHighBnp,
    highRiskEthnicity: indications?.highRiskEthnicity,
    historyOfGestationalDiabetes: indications?.historyOfGestationalDiabetes,
    dyslipidaemia: indications?.dyslipidaemia,
    chronicKidneyDisease: indications?.chronicKidneyDisease,
    historyOfSevereMentalIllness: indications?.historyOfSevereMentalIllness,
    otherText: indications?.otherText,

    smokingStatus: riskFactors?.smokingStatus,
    vapingStatus: riskFactors?.vapingStatus,
    weight: riskFactors?.weightKg?.toString(),
    height: riskFactors?.heightCm?.toString(),
    bmi: riskFactors?.bmi?.toString(),
    waistCircumference: riskFactors?.waistCircumferenceCm?.toString(),
    qRisk3Score: riskFactors?.qrisk3Score?.toString(),

    pulse: physicalExam?.pulseRate?.toString(),
    pulseRhythm: physicalExam?.pulseRhythm,
    bp1: physicalExam?.systolicBloodPressure?.toString(),
    bp2: physicalExam?.diastolicBloodPressure?.toString(),

    haemoglobin: laboratoryInvestigations?.haemoglobin?.toString(),
    totalCholesterol: laboratoryInvestigations?.totalCholesterol?.toString(),
    hdlCholesterol: laboratoryInvestigations?.hdlCholesterol?.toString(),
    nonHdlCholesterol: laboratoryInvestigations?.nonHdlCholesterol?.toString(),
    ldlCholesterol: laboratoryInvestigations?.ldlCholesterol,
    triglycerides: laboratoryInvestigations?.triglycerides?.toString(),
    hba1c: laboratoryInvestigations?.hba1c?.toString(),
    serumCreatinine: laboratoryInvestigations?.serumCreatinine?.toString(),
    egfr: laboratoryInvestigations?.egfr,
    acr:
      laboratoryInvestigations?.acr === "Not Available"
        ? "Not Available"
        : laboratoryInvestigations?.acr?.toString(),
    ntProBNP:
      laboratoryInvestigations?.ntProBNP === "Not Available"
        ? "Not Available"
        : laboratoryInvestigations?.ntProBNP?.toString(),

    outcome: ocfOutcome?.ocfOutcome,
    // Raw outcome codes — used to prefill a fresh PP/CDM registration from the
    // completed OCF outcome (mapped by code id, see KIN-1162).
    preventionReasonCodes: ocfOutcome?.preventionReasons ?? [],
    cdmDiagnosisCodes: ocfOutcome?.cdmReasons ?? [],
    // Per-code diagnosis years carried into a transferred registration
    // (OCF -> PP / OCF -> CDM). BE contract (KIN-1406): confirmedOutcome must
    // emit these maps keyed by the same reason/disease code enums. Empty until
    // BE ships the fields -> transferred years stay on the current-year default.
    preventionReasonYears: ocfOutcome?.preventionReasonYears ?? {},
    cdmDiagnosisYears: ocfOutcome?.cdmReasonYears ?? {},

    qrisk3: ocfOutcome?.preventionReasons?.includes("QRISK_GTE_20"),
    hypertenstionStage1: ocfOutcome?.preventionReasons?.includes(
      "HYPERTENSION_STAGE_1"
    ),
    hypertenstionStage1NoTargetOrganDamage:
      ocfOutcome?.preventionReasons?.includes(
        "HYPERTENSION_STAGE_1_NO_TARGET_ORGAN_DAMAGE"
      ),
    hypertenstionStage2or3: ocfOutcome?.preventionReasons?.includes(
      "HYPERTENSION_STAGE_2_OR_3"
    ),
    gestationalDiabetes: ocfOutcome?.preventionReasons?.includes(
      "GESTATIONAL_DIABETES"
    ),
    preEclampsia: ocfOutcome?.preventionReasons?.includes("PRE_ECLAMPSIA"),
    preDiabetes: ocfOutcome?.preventionReasons?.includes("PRE_DIABETES"),
    previousBNP: ocfOutcome?.preventionReasons?.includes("PREVIOUS_HIGH_BNP"),

    diabetesType2: ocfOutcome?.cdmReasons?.includes("DIABETES_TYPE_2"),
    asthma: ocfOutcome?.cdmReasons?.includes("ASTHMA"),
    copd: ocfOutcome?.cdmReasons?.includes("COPD"),
    heartFailure: ocfOutcome?.cdmReasons?.includes("HEART_FAILURE"),
    atrialFibrillation: ocfOutcome?.cdmReasons?.includes("ATRIAL_FIBRILLATION"),
    ischaemicHeartDisease: ocfOutcome?.cdmReasons?.includes(
      "ISCHAEMIC_HEART_DISEASE"
    ),
    stroke: ocfOutcome?.cdmReasons?.includes("STROKE"),
    tia: ocfOutcome?.cdmReasons?.includes("TIA"),
  };
};

export const useGetOCF = (ocfId) => {

  const tenantVal = localStorage.getItem("kinesin-tenant");

  return useQuery({
    queryKey: ["ocfItem", ocfId, tenantVal],
    queryFn: async () => {
      if (!ocfId) return null;
      const axiosInstance = await createAxiosInstance();
      const response = await axiosInstance.get(
        `${tenantVal}/cdm/reviews/${ocfId}`
      );

      return transformOCFDataFromAPI(response.data);
    },
    enabled: !!ocfId,
    meta: { errorMessage: "Error fetching OCF" },
  });
};
